import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useParams, Link } from 'react-router-dom';
import url from '../../serverurl';
import '../pecuscan-terminal.css';

// `status` is 'released' | 'cancelled' | null (still open). `escrowStatus`
// carries the raw PENDING / TREASURY_LOCKED / RELEASE / CANCEL value for the
// open case, so a treasury-backed lock can be labeled distinctly from a
// plain pending one. See resolveEscrowStatus in
// v3Blokchain/blockchainRepository.js for the source of truth.
const statusMeta = (escrow) => {
  if (escrow?.status === 'released') {
    return { label: 'Released', icon: CheckCircleIcon, tone: 'successButton' };
  }
  if (escrow?.status === 'cancelled') {
    return { label: 'Cancelled', icon: CancelIcon, tone: 'term-status-cancelled' };
  }
  if (escrow?.escrowStatus === 'TREASURY_LOCKED') {
    return { label: 'Locked · Treasury-Backed', icon: HourglassEmptyIcon, tone: 'term-status-pending' };
  }
  return { label: 'Locked · Pending', icon: HourglassEmptyIcon, tone: 'term-status-pending' };
};

const fmtDate = (d) => (d ? new Date(d).toUTCString() : null);

export default function EscrowId() {
  const { pubKey } = useParams();
  const [escrow, setEscrow] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchEscrow = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetch(`${url}/blockchain/escrow/${encodeURIComponent(pubKey)}`);
        if (res.status === 404) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const data = await res.json();
        if (!cancelled) setEscrow(data);
      } catch (err) {
        console.error(err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (pubKey) fetchEscrow();
    return () => {
      cancelled = true;
    };
  }, [pubKey]);

  if (loading) {
    return (
      <Box>
        <Navbar />
        <section className="term-detail-page">
          <div className="container">
            <div className="term-detail-loading">
              <CircularProgress sx={{ color: 'var(--term-amber)' }} size={20} />
              <span>LOADING ESCROW...</span>
            </div>
          </div>
        </section>
        <Footer />
      </Box>
    );
  }

  if (notFound || !escrow) {
    return (
      <Box>
        <Navbar />
        <section className="term-detail-page">
          <div className="container">
            <div className="term-detail-empty">▸ ESCROW NOT FOUND</div>
          </div>
        </section>
        <Footer />
      </Box>
    );
  }

  const meta = statusMeta(escrow);
  const StatusIcon = meta.icon;

  return (
    <Box>
      <Navbar />
      <section className="term-detail-page">
        <div className="container">

          {/* Header */}
          <div className="term-detail-header">
            <div className="term-detail-crumb">
              <Link to="/">HOME</Link>
              <span>/</span>
              <span>ESCROW</span>
              <span>/</span>
              <span className="term-detail-crumb-active">{escrow?.pubKey?.slice(0, 12)}…</span>
            </div>
            <div className="term-detail-title-row">
              <h1 className="term-detail-title">▸ ESCROW DETAILS</h1>
              <span className="term-status">
                <span className="term-dot"></span>{meta.label.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Summary cards */}
          <div className="term-detail-summary">
            <div className="term-summary-card">
              <div className="term-summary-lbl">AMOUNT</div>
              <div className="term-summary-val term-summary-amber">
                {escrow.amount ?? '—'} {escrow.symbol}
              </div>
            </div>
            <div className="term-summary-card">
              <div className="term-summary-lbl">STATUS</div>
              <div className="term-summary-val">{meta.label}</div>
            </div>
            <div className="term-summary-card">
              <div className="term-summary-lbl">UNLOCK ELIGIBLE</div>
              <div className="term-summary-val">{escrow.canUnlock ? 'Yes' : 'No'}</div>
            </div>
            <div className="term-summary-card">
              <div className="term-summary-lbl">SYMBOL</div>
              <div className="term-summary-val">{escrow.symbol || '—'}</div>
            </div>
          </div>

          {/* Detail panel */}
          <div className="term-detail-panel">
            <div className="term-detail-panel-head">
              <span className="term-panel-title">▸ ESCROW DATA</span>
            </div>

            <div className="term-detail-rows">

              <div className="term-detail-row">
                <div className="term-detail-lbl">Escrow Public Key</div>
                <div className="term-detail-val term-addr">{escrow.pubKey}</div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">Status</div>
                <div className="term-detail-val">
                  <span className={meta.tone}>
                    <i><StatusIcon /></i>
                    {meta.label}
                  </span>
                </div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">From</div>
                <div className="term-detail-val term-addr">
                  {escrow.from || <span className="term-detail-empty-inline">—</span>}
                </div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">To</div>
                <div className="term-detail-val term-addr">
                  {escrow.to || <span className="term-detail-empty-inline">—</span>}
                </div>
              </div>

              <div className="term-detail-divider" />

              <div className="term-detail-row">
                <div className="term-detail-lbl">Amount</div>
                <div className="term-detail-val term-amount">
                  {escrow.amount ?? '—'} {escrow.symbol}
                </div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">Locked At</div>
                <div className="term-detail-val">
                  {escrow.lockedAt ? (
                    <>
                      <i><AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} /></i>
                      {fmtDate(escrow.lockedAt)}
                    </>
                  ) : (
                    <span className="term-detail-empty-inline">—</span>
                  )}
                </div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">Unlock At</div>
                <div className="term-detail-val">
                  {escrow.unlockAt ? (
                    <>
                      <i><AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} /></i>
                      {fmtDate(escrow.unlockAt)}
                    </>
                  ) : (
                    <span className="term-detail-empty-inline">—</span>
                  )}
                </div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">Unlocked At</div>
                <div className="term-detail-val">
                  {escrow.unlockedAt ? (
                    <>
                      <i><AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} /></i>
                      {fmtDate(escrow.unlockedAt)}
                    </>
                  ) : (
                    <span className="term-detail-empty-inline">—</span>
                  )}
                </div>
              </div>

              <div className="term-detail-divider" />

              <div className="term-detail-row">
                <div className="term-detail-lbl">Public Note</div>
                <div className="term-detail-val">
                  {escrow.reason || <span className="term-detail-empty-inline">—</span>}
                </div>
              </div>

              <div className="term-detail-row">
                <div className="term-detail-lbl">Private Note</div>
                <div className="term-detail-val">
                  {escrow.privateNote || <span className="term-detail-empty-inline">—</span>}
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>
      <Footer />
    </Box>
  );
}
